import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AdminModerationContextMessage,
  AdminModerationFlagItem,
  AdminModerationFlagListResponse,
  AdminModerationReportContextResponse,
  AdminModerationReportItem,
  AdminModerationReportListResponse,
} from '@shared/types';
import { MODERATION } from '@shared/constants';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import Pagination from '../../components/Pagination';
import Pill from '../../components/Pill';
import ConfirmModal from '../../components/ConfirmModal';
import { ApiError } from '../../services/api';
import {
  getModerationReportContext,
  listModerationFlags,
  listModerationReports,
  resolveModerationReport,
} from '../../services/moderation';
import { banUser } from '../../services/users';
import { useToast } from '../../stores/toastStore';

const PAGE_SIZE = MODERATION.REPORTS_PAGE_SIZE;
const BAN_REASON = 'DM abuse report';

type ReportStatusFilter = '' | 'pending' | 'reviewed' | 'dismissed';
type FlagActionFilter = '' | 'masked' | 'blocked';
type Tab = 'reports' | 'flagged';

/**
 * DM moderation surface. Two tabs:
 *   - Reports: user-submitted DM reports → triage (view context / resolve / ban).
 *   - Flagged: auto-filter (profanity) events → masked / blocked audit trail.
 *
 * Each tab owns its own `{ status: 'loading' | 'ready' | 'error' }` state
 * machine + pagination, mirroring `routes/users/List.tsx`.
 */
export default function Moderation() {
  const [tab, setTab] = useState<Tab>('reports');

  return (
    <div className="space-y-4" data-testid="moderation-screen">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">Moderation</h1>
      </div>

      <div
        className="flex gap-1 border-b border-slate-200"
        role="tablist"
        aria-label="Moderation views"
      >
        <TabButton
          active={tab === 'reports'}
          onClick={() => setTab('reports')}
          testId="moderation-tab-reports"
        >
          Reports
        </TabButton>
        <TabButton
          active={tab === 'flagged'}
          onClick={() => setTab('flagged')}
          testId="moderation-tab-flagged"
        >
          Flagged
        </TabButton>
      </div>

      {tab === 'reports' ? <ReportsTab /> : <FlaggedTab />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className={
        '-mb-px px-4 py-2 text-sm font-medium border-b-2 transition-colors ' +
        (active
          ? 'border-slate-800 text-slate-900'
          : 'border-transparent text-slate-500 hover:text-slate-800')
      }
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Reports tab
// ---------------------------------------------------------------------------

function ReportsTab() {
  const { showToast } = useToast();

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<ReportStatusFilter>('');

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: AdminModerationReportListResponse }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  // Per-row in-flight action guard so buttons disable while resolving/banning.
  const [busyRowId, setBusyRowId] = useState<string | null>(null);

  // Context modal state.
  const [contextReportId, setContextReportId] = useState<string | null>(null);

  // Ban confirm state.
  const [banTarget, setBanTarget] = useState<{
    reportId: string;
    userId: string;
    displayName: string;
  } | null>(null);

  // Reset to page 1 when the filter changes.
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  const reqCounterRef = useRef(0);
  const fetchReports = useCallback(async () => {
    const reqId = ++reqCounterRef.current;
    setState({ status: 'loading' });
    try {
      const data = await listModerationReports({
        page,
        limit: PAGE_SIZE,
        status: statusFilter || undefined,
      });
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'ready', data });
    } catch (err) {
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'error', message: toMessage(err, 'Failed to load reports') });
    }
  }, [page, statusFilter]);

  useEffect(() => {
    void fetchReports();
  }, [fetchReports]);

  const rows = useMemo<AdminModerationReportItem[]>(
    () => (state.status === 'ready' ? state.data.data : []),
    [state],
  );

  const totalPages = useMemo(() => {
    if (state.status !== 'ready') return 1;
    if (state.data.total === 0) return 1;
    return Math.max(1, Math.ceil(state.data.total / state.data.limit));
  }, [state]);

  const handleResolve = useCallback(
    async (report: AdminModerationReportItem, status: 'reviewed' | 'dismissed') => {
      setBusyRowId(report.id);
      try {
        await resolveModerationReport(report.id, status);
        showToast(
          status === 'reviewed' ? 'Report marked reviewed.' : 'Report dismissed.',
          'success',
        );
        await fetchReports();
      } catch (err) {
        showToast(toMessage(err, 'Failed to resolve report'), 'error');
      } finally {
        setBusyRowId(null);
      }
    },
    [fetchReports, showToast],
  );

  const handleConfirmBan = useCallback(async () => {
    if (!banTarget) return;
    setBusyRowId(banTarget.reportId);
    try {
      await banUser(banTarget.userId, BAN_REASON);
      showToast(`Banned ${banTarget.displayName}.`, 'success');
      setBanTarget(null);
      await fetchReports();
    } catch (err) {
      showToast(toMessage(err, 'Failed to ban user'), 'error');
    } finally {
      setBusyRowId(null);
    }
  }, [banTarget, fetchReports, showToast]);

  const columns: DataTableColumn<AdminModerationReportItem>[] = useMemo(
    () => [
      {
        key: 'reportedUser',
        header: 'Reported user',
        render: (r) => (
          <div className="flex flex-wrap items-center gap-1">
            <span className="font-medium text-slate-900">
              {r.reportedUser?.displayName ?? '—'}
            </span>
            {r.reportedUser?.isBanned && (
              <Pill variant="danger" size="sm" testId={`moderation-reported-banned-${r.id}`}>
                Banned
              </Pill>
            )}
          </div>
        ),
      },
      {
        key: 'reporter',
        header: 'Reporter',
        render: (r) => (
          <span className="text-slate-700">{r.reporter?.displayName ?? '—'}</span>
        ),
      },
      {
        key: 'reason',
        header: 'Reason',
        render: (r) => (
          <Pill variant="neutral" size="sm">
            {r.reason.replace(/_/g, ' ')}
          </Pill>
        ),
      },
      {
        key: 'messageText',
        header: 'Message',
        render: (r) => (
          <span
            className={
              'text-sm ' + (r.messageText == null ? 'italic text-slate-400' : 'text-slate-700')
            }
          >
            {r.messageText ?? '(unavailable)'}
          </span>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (r) => <ReportStatusPill status={r.status} />,
      },
      {
        key: 'createdAt',
        header: 'Created',
        render: (r) => (
          <span className="text-xs text-slate-500">{formatDate(r.createdAt)}</span>
        ),
      },
      {
        key: 'actions',
        header: 'Actions',
        render: (r) => {
          const terminal = r.status !== 'pending';
          const rowBusy = busyRowId === r.id;
          return (
            <div className="flex flex-wrap items-center gap-1">
              <RowButton
                onClick={() => setContextReportId(r.id)}
                testId={`moderation-view-context-${r.id}`}
              >
                View context
              </RowButton>
              <RowButton
                onClick={() => void handleResolve(r, 'reviewed')}
                disabled={terminal || rowBusy}
                testId={`moderation-mark-reviewed-${r.id}`}
              >
                Mark reviewed
              </RowButton>
              <RowButton
                onClick={() => void handleResolve(r, 'dismissed')}
                disabled={terminal || rowBusy}
                testId={`moderation-dismiss-${r.id}`}
              >
                Dismiss
              </RowButton>
              <RowButton
                tone="danger"
                onClick={() =>
                  r.reportedUser &&
                  setBanTarget({
                    reportId: r.id,
                    userId: r.reportedUser.id,
                    displayName: r.reportedUser.displayName,
                  })
                }
                disabled={!r.reportedUser || r.reportedUser.isBanned || rowBusy}
                testId={`moderation-ban-${r.id}`}
              >
                Ban user
              </RowButton>
            </div>
          );
        },
      },
    ],
    [busyRowId, handleResolve],
  );

  return (
    <div className="space-y-4">
      <div className="bg-white rounded border border-slate-200 p-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-slate-600">
          Status
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ReportStatusFilter)}
            data-testid="moderation-status-filter"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </label>
      </div>

      {state.status === 'error' && (
        <div
          role="alert"
          data-testid="moderation-reports-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
        >
          {state.message}
        </div>
      )}

      <DataTable<AdminModerationReportItem>
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={state.status === 'loading'}
        empty={statusFilter ? 'No reports match that filter.' : 'No reports yet.'}
        testId="moderation-reports-table"
      />

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        disabled={state.status === 'loading'}
      />

      {contextReportId && (
        <ContextModal
          reportId={contextReportId}
          onClose={() => setContextReportId(null)}
        />
      )}

      <ConfirmModal
        open={banTarget != null}
        title="Ban user"
        description={
          banTarget
            ? `Ban ${banTarget.displayName}? This revokes all their tokens and disables the account.`
            : ''
        }
        confirmLabel="Ban user"
        danger
        busy={busyRowId != null && busyRowId === banTarget?.reportId}
        onConfirm={() => void handleConfirmBan()}
        onCancel={() => setBanTarget(null)}
      />
    </div>
  );
}

function ReportStatusPill({ status }: { status: AdminModerationReportItem['status'] }) {
  const variant =
    status === 'pending' ? 'warning' : status === 'reviewed' ? 'success' : 'neutral';
  return (
    <Pill variant={variant} size="sm" testId={`moderation-status-${status}`}>
      {status}
    </Pill>
  );
}

// ---------------------------------------------------------------------------
// Context modal
// ---------------------------------------------------------------------------

function ContextModal({
  reportId,
  onClose,
}: {
  reportId: string;
  onClose: () => void;
}) {
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: AdminModerationReportContextResponse }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    getModerationReportContext(reportId)
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', data });
      })
      .catch((err) => {
        if (!cancelled)
          setState({ status: 'error', message: toMessage(err, 'Failed to load context') });
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="moderation-context-title"
      data-testid="moderation-context-modal"
    >
      <div
        className="absolute inset-0 bg-slate-900/50"
        data-testid="moderation-context-backdrop"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-lg shadow-lg max-w-lg w-full p-5 space-y-3 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2
            id="moderation-context-title"
            className="text-base font-semibold text-slate-900"
          >
            Conversation context
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-testid="moderation-context-close"
            className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50"
          >
            Close
          </button>
        </div>

        {state.status === 'loading' && (
          <p className="text-sm text-slate-500" data-testid="moderation-context-loading">
            Loading…
          </p>
        )}

        {state.status === 'error' && (
          <div
            role="alert"
            data-testid="moderation-context-error"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
          >
            {state.message}
          </div>
        )}

        {state.status === 'ready' && (
          <div className="space-y-2" data-testid="moderation-context-messages">
            {state.data.messages.length === 0 ? (
              <p className="text-sm text-slate-500">No surrounding messages.</p>
            ) : (
              state.data.messages.map((m) => <ContextMessageRow key={m.id} message={m} />)
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ContextMessageRow({ message }: { message: AdminModerationContextMessage }) {
  return (
    <div
      data-testid={`moderation-context-message-${message.id}`}
      className={
        'rounded border px-3 py-2 ' +
        (message.isReportedMessage
          ? 'border-red-300 bg-red-50'
          : 'border-slate-200 bg-slate-50')
      }
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-slate-700">
          {message.senderDisplayName}
        </span>
        <span className="text-[10px] text-slate-400">{formatDate(message.createdAt)}</span>
      </div>
      <div
        className={
          'text-sm ' + (message.text == null ? 'italic text-slate-400' : 'text-slate-800')
        }
      >
        {message.text ?? '(unavailable)'}
      </div>
      {message.isReportedMessage && (
        <Pill variant="danger" size="sm" className="mt-1">
          Reported
        </Pill>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flagged tab
// ---------------------------------------------------------------------------

function FlaggedTab() {
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState<FlagActionFilter>('');

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; data: AdminModerationFlagListResponse }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  useEffect(() => {
    setPage(1);
  }, [actionFilter]);

  const reqCounterRef = useRef(0);
  const fetchFlags = useCallback(async () => {
    const reqId = ++reqCounterRef.current;
    setState({ status: 'loading' });
    try {
      const data = await listModerationFlags({
        page,
        limit: PAGE_SIZE,
        action: actionFilter || undefined,
      });
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'ready', data });
    } catch (err) {
      if (reqId !== reqCounterRef.current) return;
      setState({ status: 'error', message: toMessage(err, 'Failed to load flags') });
    }
  }, [page, actionFilter]);

  useEffect(() => {
    void fetchFlags();
  }, [fetchFlags]);

  const rows = useMemo<AdminModerationFlagItem[]>(
    () => (state.status === 'ready' ? state.data.data : []),
    [state],
  );

  const totalPages = useMemo(() => {
    if (state.status !== 'ready') return 1;
    if (state.data.total === 0) return 1;
    return Math.max(1, Math.ceil(state.data.total / state.data.limit));
  }, [state]);

  const columns: DataTableColumn<AdminModerationFlagItem>[] = useMemo(
    () => [
      {
        key: 'sender',
        header: 'Sender',
        render: (f) => (
          <div className="flex flex-wrap items-center gap-1">
            <span className="font-medium text-slate-900">
              {f.sender?.displayName ?? '—'}
            </span>
            {f.sender?.isBanned && (
              <Pill variant="danger" size="sm" testId={`moderation-flag-sender-banned-${f.id}`}>
                Banned
              </Pill>
            )}
          </div>
        ),
      },
      {
        key: 'action',
        header: 'Action',
        render: (f) => (
          <Pill
            variant={f.action === 'blocked' ? 'danger' : 'warning'}
            size="sm"
            testId={`moderation-flag-action-${f.id}`}
          >
            {f.action}
          </Pill>
        ),
      },
      {
        key: 'originalText',
        header: 'Original',
        render: (f) => <span className="text-sm text-slate-700">{f.originalText}</span>,
      },
      {
        key: 'maskedText',
        header: 'Masked',
        render: (f) => (
          <span
            className={
              'text-sm ' + (f.maskedText == null ? 'text-slate-400' : 'text-slate-700')
            }
          >
            {f.maskedText ?? '—'}
          </span>
        ),
      },
      {
        key: 'matchedTokens',
        header: 'Matched tokens',
        render: (f) => (
          <span className="font-mono text-xs text-slate-700">
            {f.matchedTokens.join(', ')}
          </span>
        ),
      },
      {
        key: 'language',
        header: 'Lang',
        render: (f) => (
          <span className="font-mono text-xs text-slate-700 uppercase">{f.language}</span>
        ),
      },
      {
        key: 'createdAt',
        header: 'Created',
        render: (f) => (
          <span className="text-xs text-slate-500">{formatDate(f.createdAt)}</span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <div className="bg-white rounded border border-slate-200 p-3 flex flex-wrap items-end gap-3">
        <label className="flex flex-col text-xs text-slate-600">
          Action
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value as FlagActionFilter)}
            data-testid="moderation-action-filter"
            className="mt-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            <option value="">All</option>
            <option value="masked">Masked</option>
            {/* The filter has one tier now — every new row is `masked`. This
                option only ever returns rows written under the retired
                two-tier policy, and empties once the TTL expires them. */}
            <option value="blocked">Blocked (legacy)</option>
          </select>
        </label>
      </div>

      {state.status === 'error' && (
        <div
          role="alert"
          data-testid="moderation-flags-error"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
        >
          {state.message}
        </div>
      )}

      <DataTable<AdminModerationFlagItem>
        columns={columns}
        rows={rows}
        rowKey={(f) => f.id}
        loading={state.status === 'loading'}
        empty={actionFilter ? 'No flags match that filter.' : 'No flagged messages yet.'}
        testId="moderation-flags-table"
      />

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        disabled={state.status === 'loading'}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function RowButton({
  onClick,
  disabled,
  tone,
  testId,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  tone?: 'danger';
  testId: string;
  children: React.ReactNode;
}) {
  const toneClasses =
    tone === 'danger'
      ? 'border-red-300 text-red-700 hover:bg-red-50'
      : 'border-slate-300 text-slate-700 hover:bg-slate-50';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={
        'text-xs border rounded px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed ' +
        toneClasses
      }
    >
      {children}
    </button>
  );
}

function toMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}
